/**
 * 设备指纹信息接口
 */
interface DeviceFingerprint {
  id: string
  userAgent: string
  screen: string
  timezone: string
  language: string
  platform: string
  timestamp: number
  version: string
}

/**
 * 生成设备指纹哈希
 */
async function generateFingerprint(): Promise<string> {
  const components: string[] = []

  try {
    // 1. 基础浏览器信息
    components.push(navigator.userAgent || 'unknown')
    components.push(navigator.language || 'unknown')
    components.push(navigator.platform || 'unknown')
    components.push((navigator as any).hardwareConcurrency?.toString() || 'unknown')

    // 2. 屏幕信息
    const screen = window.screen
    components.push(`${screen.width}x${screen.height}x${screen.colorDepth}`)
    components.push(screen.availWidth?.toString() || 'unknown')
    components.push(screen.availHeight?.toString() || 'unknown')

    // 3. 时区信息
    components.push(Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown')
    components.push(new Date().getTimezoneOffset().toString())

    // 4. 渲染信息
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillText('Device fingerprint test 🔍', 2, 2)
      components.push(canvas.toDataURL().slice(0, 100)) // 取前100字符避免过长
    }

    // 5. WebGL信息
    try {
      const gl =
        canvas.getContext('webgl') ||
        (canvas.getContext('experimental-webgl') as WebGLRenderingContext)
      if (gl && 'getParameter' in gl) {
        const renderer = gl.getParameter(gl.RENDERER)
        const vendor = gl.getParameter(gl.VENDOR)
        components.push(renderer || 'unknown')
        components.push(vendor || 'unknown')
      }
    } catch {
      components.push('webgl-error')
    }

    // 6. 音频指纹
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const analyser = audioContext.createAnalyser()
      const gainNode = audioContext.createGain()

      oscillator.connect(analyser)
      analyser.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.setValueAtTime(10000, audioContext.currentTime)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)

      const bufferLength = analyser.frequencyBinCount
      const dataArray = new Uint8Array(bufferLength)
      analyser.getByteFrequencyData(dataArray)

      components.push(Array.from(dataArray.slice(0, 10)).join(''))

      oscillator.disconnect()
      audioContext.close()
    } catch {
      components.push('audio-error')
    }

    // 7. 字体检测
    const fonts = ['Arial', 'Helvetica', 'Times', 'Courier', 'Georgia', 'Verdana']
    const fontDetection = fonts
      .map((font) => {
        const span = document.createElement('span')
        span.style.fontFamily = font
        span.style.fontSize = '100px'
        span.style.visibility = 'hidden'
        span.style.position = 'absolute'
        span.textContent = 'mmMwWLliI0O&1'
        document.body.appendChild(span)
        const width = span.offsetWidth
        document.body.removeChild(span)
        return width.toString()
      })
      .join(',')
    components.push(fontDetection)

    // 8. 存储能力检测
    const storageTests = []
    try {
      storageTests.push(!!window.localStorage ? '1' : '0')
      storageTests.push(!!window.sessionStorage ? '1' : '0')
      storageTests.push(!!window.indexedDB ? '1' : '0')
    } catch {
      storageTests.push('storage-error')
    }
    components.push(storageTests.join(''))
  } catch (error) {
    console.warn('Fingerprint generation error:', error)
    components.push('fingerprint-error')
  }

  // 生成最终指纹
  const fingerprintString = components.join('|')
  return await hashString(fingerprintString)
}

/**
 * 使用Web Crypto API生成SHA-256哈希
 */
async function hashString(str: string): Promise<string> {
  try {
    const encoder = new TextEncoder()
    const data = encoder.encode(str)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    // 降级到简单哈希
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // 转换为32位整数
    }
    return Math.abs(hash).toString(16)
  }
}

/**
 * 生成备用UUID
 */
function generateFallbackId(): string {
  if (crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // 手动生成UUID v4格式
  const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
  return template.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * 健壮的设备ID生成方法
 * 优先级：缓存 > 指纹 > UUID
 */
export async function generateDeviceId(): Promise<string> {
  const STORAGE_KEY = '_device_fingerprint_'
  const CACHE_DURATION = 30 * 24 * 60 * 60 * 1000 * 365 * 2 // 2年缓存

  try {
    // 1. 尝试从localStorage获取缓存
    const cached = localStorage.getItem(STORAGE_KEY)
    if (cached) {
      try {
        const data: DeviceFingerprint = JSON.parse(cached)
        // 检查缓存是否过期
        if (Date.now() - data.timestamp < CACHE_DURATION) {
          return data.id
        }
      } catch {
        // 缓存格式错误，清除
        localStorage.removeItem(STORAGE_KEY)
      }
    }

    // 2. 生成新的设备指纹
    let deviceId: string

    try {
      // 尝试生成基于指纹的ID
      const fingerprint = await generateFingerprint()
      deviceId = `fp_pc_${fingerprint.slice(0, 32)}` // 取前32位
    } catch (error) {
      console.warn('Fingerprint generation failed, using fallback:', error)
      // 降级到UUID
      deviceId = `uuid_pc_${generateFallbackId()}`
    }

    // 3. 保存到localStorage
    const fingerprintData: DeviceFingerprint = {
      id: deviceId,
      userAgent: navigator.userAgent,
      screen: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      timestamp: Date.now(),
      version: '1.0.1'
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fingerprintData))
    } catch {
      console.warn('Failed to save device fingerprint to localStorage')
    }

    return deviceId
  } catch (error) {
    console.error('Device ID generation failed:', error)
    // 最后的降级方案
    return `fallback_${Date.now()}_${Math.random().toString(36).slice(2)}`
  }
}

/**
 * 获取设备指纹详细信息
 */
export function getDeviceFingerprintInfo(): DeviceFingerprint | null {
  const STORAGE_KEY = '_device_fingerprint_'

  try {
    const cached = localStorage.getItem(STORAGE_KEY)
    if (cached) {
      return JSON.parse(cached)
    }
  } catch {
    console.warn('Failed to parse device fingerprint info')
  }

  return null
}

/**
 * 清除设备指纹缓存
 */
export function clearDeviceFingerprint(): void {
  const STORAGE_KEY = '_device_fingerprint_'
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    console.warn('Failed to clear device fingerprint')
  }
}
